// Concept-Graph Propagation — Boosts review priority for related questions
// Multi-cert: boost cache and Firebase paths are cert-scoped via certPath()

import { certPath, read, write } from './firebase-store.js';
import { PROPAGATION_MIN_OVERLAP, PROPAGATION_FULL_BOOST, PROPAGATION_PARTIAL_BOOST } from './constants.js';
import { isGraphReady, getSkillsForConcept, getConceptsForSkill } from './knowledge-graph.js';

// In-memory boost cache: { [certId]: { [userId]: { [boostKey]: { boost, reason, ts } } } }
const _boostCache = {};

/**
 * Trigger concept propagation after an answer (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} questionId - the question just answered
 * @param {Array<string>} conceptTags - concept tags from the question
 * @param {boolean} correct
 * @returns {object|null} { boosted: number, reduced: number } or null if no tags
 */
export async function triggerPropagation(userId, certId, questionId, conceptTags, correct) {
  if (!conceptTags || conceptTags.length === 0) return null;

  if (!_boostCache[certId]) _boostCache[certId] = {};
  if (!_boostCache[certId][userId]) _boostCache[certId][userId] = {};

  // Load existing boosts
  let boosts;
  try {
    boosts = await read(certPath(userId, certId, 'boosts')) || {};
  } catch (e) {
    boosts = _boostCache[certId][userId];
  }

  // Merge cached boosts
  Object.assign(boosts, _boostCache[certId][userId]);

  let boostedCount = 0;
  let reducedCount = 0;
  const tagSet = new Set(conceptTags);

  if (!correct) {
    // Wrong answer: boost related questions
    for (const tag of conceptTags) {
      const boostKey = 'concept:' + tag;
      const existing = boosts[boostKey] || { boost: 0, count: 0 };
      boosts[boostKey] = {
        boost: Math.min(existing.boost + PROPAGATION_FULL_BOOST, 2.0),
        count: existing.count + 1,
        lastFailed: new Date().toISOString(),
        sourceQuestion: questionId
      };
      boostedCount++;
    }

    // Knowledge graph: boost related skills' concepts with partial boost
    if (isGraphReady(certId)) {
      const relatedConcepts = new Set();
      for (const tag of conceptTags) {
        const skills = getSkillsForConcept(certId, tag);
        for (const skill of skills) {
          const neighborConcepts = getConceptsForSkill(certId, skill);
          for (const nc of neighborConcepts) {
            if (!tagSet.has(nc)) relatedConcepts.add(nc);
          }
        }
      }
      for (const rc of relatedConcepts) {
        const boostKey = 'concept:' + rc;
        const existing = boosts[boostKey] || { boost: 0, count: 0 };
        boosts[boostKey] = {
          boost: Math.min(existing.boost + PROPAGATION_PARTIAL_BOOST, 1.5),
          count: existing.count + 1,
          lastFailed: new Date().toISOString(),
          sourceQuestion: questionId,
          indirect: true
        };
        boostedCount++;
      }
    }
  } else {
    // Correct answer: reduce boost on these concepts
    for (const tag of conceptTags) {
      const boostKey = 'concept:' + tag;
      if (boosts[boostKey] && boosts[boostKey].boost > 0) {
        boosts[boostKey].boost = Math.max(0, boosts[boostKey].boost - PROPAGATION_PARTIAL_BOOST);
        if (boosts[boostKey].boost === 0) {
          delete boosts[boostKey];
        }
        reducedCount++;
      }
    }
  }

  // Save
  _boostCache[certId][userId] = boosts;
  try {
    await write(certPath(userId, certId, 'boosts'), boosts);
  } catch (e) {
    // Firebase not initialized — in-memory only
  }

  return { boosted: boostedCount, reduced: reducedCount };
}

/**
 * Get the priority boost for a specific question based on its concept tags (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {Array<string>} conceptTags
 * @returns {number} boost value (0 = no boost)
 */
export async function getConceptBoost(userId, certId, conceptTags) {
  if (!conceptTags || conceptTags.length === 0) return 0;

  let boosts;
  try {
    boosts = await read(certPath(userId, certId, 'boosts')) || {};
  } catch (e) {
    boosts = (_boostCache[certId] && _boostCache[certId][userId]) || {};
  }

  let totalBoost = 0;
  for (const tag of conceptTags) {
    const boostKey = 'concept:' + tag;
    if (boosts[boostKey]) {
      totalBoost += boosts[boostKey].boost;
    }
  }

  return totalBoost;
}

/**
 * Get questions related to a given question by concept overlap.
 * Stateless — no cert-scoping needed.
 *
 * @param {string} questionId
 * @param {Array<string>} conceptTags
 * @param {Array<object>} questionBank
 * @param {number} minOverlap
 * @returns {Array<{id, overlap}>}
 */
export function getRelatedQuestions(questionId, conceptTags, questionBank, minOverlap) {
  if (!conceptTags || conceptTags.length === 0 || !questionBank) return [];

  const min = minOverlap || PROPAGATION_MIN_OVERLAP;
  const tagSet = new Set(conceptTags);
  const related = [];

  for (const q of questionBank) {
    if (q.id === questionId) continue;
    const qTags = q.conceptTags || [];
    const overlap = qTags.filter(t => tagSet.has(t)).length;
    if (overlap >= min) {
      related.push({ id: q.id, overlap });
    }
  }

  return related.sort((a, b) => b.overlap - a.overlap);
}
